import { ChangeEvent, FC, FocusEvent, useCallback, useEffect, useRef, useState } from 'react';

import { Box, FormLabel, TextField, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';
import { CountryCode } from 'libphonenumber-js';

import Loader from 'src/components/Loader/Loader';
import ValidateButton from 'src/components/ValidateButton/ValidateButton';
import { useCountries, useValidatePhoneNumber } from 'src/hooks';

import CountryDropDown from './components/CountryDropDown';
import validatePhoneNumber from './NPIPhoneInput.utils';

interface NPIPhoneInputProps {
    fieldName: string;
    label?: string;
    placeholder?: string;
    /**
     * Default country to select (ISO2 code)
     */
    defaultCountry?: string;
    disabled?: boolean;
    containerStyle?: object;
    dataTestId?: string;
    onChange?: (e164Value: string, dialCode: string, number: string) => void;
}

const DEFAULT_REGION = (process.env.REGION as CountryCode) ?? 'IE';

const NPIPhoneInput: FC<NPIPhoneInputProps> = ({
    fieldName,
    label = 'Phone Number',
    placeholder = 'Enter phone number',
    defaultCountry = DEFAULT_REGION,
    disabled = false,
    containerStyle,
    dataTestId = 'phone-input',
    onChange,
}) => {
    const [field, meta, helpers] = useField(fieldName);
    const { setValue } = helpers;

    const { countries, loadingCountries } = useCountries();
    const [selectedCountryName, setSelectedCountryName] = useState<string>('Ireland');
    const [areaCode, setAreaCode] = useState<string>('IE');
    const [phoneNumber, setPhoneNumber] = useState<string>('');

    const { onValidatePhoneNumber, validatingPhoneNumber } = useValidatePhoneNumber();

    // Use ref to store the latest onChange callback to prevent infinite loops
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    // Use ref to track validation state to prevent multiple simultaneous validations
    const isValidatingRef = useRef(false);

    // Initialize selected country based on default country
    useEffect(() => {
        const country = countries.find((country) => {
            return country.country === defaultCountry;
        });
        if (country) {
            setSelectedCountryName(country.name);
            setAreaCode(country.country);
        }
    }, [defaultCountry, countries]);

    // Parse existing field value if present
    useEffect(() => {
        if (field.value?.startsWith('+')) {
            // Try to split E.164 format into dial code and number
            // Find all countries that match the calling code
            const matchingCountries = countries.filter((country) => {
                return field.value.startsWith(country.callingCode);
            });

            if (matchingCountries.length > 0) {
                // Define priority countries for shared calling codes
                const priorityCountries = {
                    '+44': 'GB', // United Kingdom takes priority over Guernsey, Jersey, etc.
                    '+1': 'US', // United States takes priority over Canada
                    '+7': 'RU', // Russia takes priority over Kazakhstan
                };

                const [firstMatch] = matchingCountries; // Default to first match
                let selectedCountry = firstMatch;

                // Check if this is a shared calling code with a priority country
                const { callingCode } = firstMatch;
                const priorityCountryCode =
                    priorityCountries[callingCode as keyof typeof priorityCountries];

                if (priorityCountryCode) {
                    const priorityCountry = matchingCountries.find((country) => {
                        return country.country === priorityCountryCode;
                    });
                    if (priorityCountry) {
                        selectedCountry = priorityCountry;
                    }
                }

                setSelectedCountryName(selectedCountry.name);
                setAreaCode(selectedCountry.country);
                setPhoneNumber(field.value.substring(selectedCountry.callingCode.length));
            }
        }
    }, [field.value, countries]);

    // Get current country's calling code
    const currentCountry = countries.find((country) => {
        return country.country === areaCode;
    });
    const dialCode = currentCountry?.callingCode || '';

    // Update formik field when area code or number changes (with debounce-like protection)
    const lastE164ValueRef = useRef('');
    useEffect(() => {
        const e164Value = phoneNumber ? `${dialCode}${phoneNumber}` : '';

        if (e164Value !== field.value && e164Value !== lastE164ValueRef.current) {
            lastE164ValueRef.current = e164Value;
            setValue(e164Value);
        }
    }, [dialCode, phoneNumber, field.value, setValue]);

    // Simple effect for onChange callback
    useEffect(() => {
        if (onChangeRef.current) {
            const e164Value = phoneNumber ? `${dialCode}${phoneNumber}` : '';
            onChangeRef.current(e164Value, dialCode, phoneNumber);
        }
    }, [dialCode, phoneNumber]);

    const handleNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        // Only allow numbers
        const value = event.target.value.replace(/\D/g, '');
        setPhoneNumber(value);
    }, []);

    const handleBlur = useCallback(() => {
        field.onBlur({ target: { name: fieldName } } as FocusEvent<HTMLInputElement>);
    }, [field, fieldName]);

    const hasError = Boolean(meta.touched && meta.error);
    const { palette } = useTheme();
    const bgColour = hasError ? palette.error.main : palette.border.light;

    const validateNumber = useCallback(async () => {
        // Prevent multiple simultaneous validations
        if (isValidatingRef.current) {
            return;
        }

        isValidatingRef.current = true;

        try {
            const result = await validatePhoneNumber({
                value: field.value,
                onValidatePhoneNumber,
                setError: helpers.setError,
                setValue: helpers.setValue,
            });
            return result;
        } finally {
            isValidatingRef.current = false;
        }
    }, [field.value, onValidatePhoneNumber, helpers.setError, helpers.setValue]);

    return (
        <>
            {(loadingCountries || validatingPhoneNumber) && <Loader />}
            <Box display="flex" flexDirection="column" flex={0.49} {...containerStyle}>
                {label && (
                    <FormLabel
                        sx={{
                            marginBottom: '5px',
                            color: '#000',
                            fontWeight: 600,
                            display: 'block',
                        }}
                    >
                        {label}
                    </FormLabel>
                )}

                <Box
                    className={`search-input-box ${hasError ? 'input-error' : ''}`}
                    display="flex"
                    flexDirection="row"
                    height="51px"
                    borderRadius="8px"
                    overflow="hidden"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <CountryDropDown
                        countries={countries}
                        selectedCountryName={selectedCountryName}
                        setSelectedCountryName={setSelectedCountryName}
                        setAreaCode={setAreaCode}
                        disabled={disabled}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            backgroundColor: '#f5f5f5',
                            minWidth: '60px',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: disabled ? '#999' : '#666',
                                fontWeight: 500,
                            }}
                        >
                            {dialCode}
                        </Typography>
                    </Box>

                    <TextField
                        value={phoneNumber}
                        onChange={handleNumberChange}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        disabled={disabled}
                        autoComplete="off"
                        variant="standard"
                        slotProps={{
                            input: {
                                'data-testid': `${dataTestId}-number`,
                                'aria-label': 'Phone number',
                                disableUnderline: true,
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                            },
                        }}
                        error={hasError}
                        sx={{
                            '& input[type=number]': {
                                '-moz-appearance': 'textfield',
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                            },
                        }}
                    />

                    <ValidateButton
                        onClick={validateNumber}
                        disabled={
                            !field.value ||
                            disabled ||
                            validatingPhoneNumber ||
                            isValidatingRef.current
                        }
                        searchText="Validate"
                        dataTestId={dataTestId}
                        sx={{ bgcolor: bgColour }}
                    />
                </Box>

                {hasError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{
                            marginTop: '4px',
                            display: 'block',
                            fontSize: '0.75rem',
                        }}
                    >
                        {meta.error}
                    </Typography>
                )}
            </Box>
        </>
    );
};

export default NPIPhoneInput;
